import classNames from 'classnames';
import { useEffect, useRef, type ReactNode } from 'react';
import { MarketEmptyPanel, MarketLoadingPanel } from '~/features/shared/market-content';
import type { B2BMessage } from '../types';

type Props = {
  messages: B2BMessage[];
  // UserInfo.id of the viewer, not EmployerUser.id. The backend fills
  // JobMessage.sender_user_id from common.user_info.id, so comparing to
  // EmployerUser.id would mis-classify every own message as received.
  currentUserInfoId: number | undefined;
  loading: boolean;
  renderMessageContext?: (message: B2BMessage) => ReactNode;
};

const formatMessageTime = (dateStr: string): string => {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

export const MessagesSection = ({
  messages,
  currentUserInfoId,
  loading,
  renderMessageContext,
}: Props) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading) {
    return <MarketLoadingPanel label="Loading messages" className="flex-1" />;
  }

  if (messages.length === 0) {
    return <MarketEmptyPanel title="No messages yet" className="flex-1" />;
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
      {messages.map((msg) => {
        // B2B: "sent" means the message came from this viewer.
        // Both sides are employers, so senderEmployerId alone is ambiguous.
        // senderUserId is backed by common.user_info.id, so the viewer's
        // UserInfo.id is the unambiguous identity key.
        const isSent = currentUserInfoId !== undefined && msg.senderUserId === currentUserInfoId;
        return (
          <div
            key={msg.id}
            className={classNames('max-w-[85%]', isSent ? 'self-end' : 'self-start')}
          >
            <div
              className={classNames(
                // MCV-1559: `#root { text-align: center }` (styles.css) is
                // inherited by every descendant that doesn't set its own
                // text-align, and nothing here did — so bubble text rendered
                // centered. `text-left` must stay on this element (not a
                // parent) because that inherited `center` beats anything we
                // don't override at the bubble itself.
                //
                // Both sides read left-aligned, sent included: QA revised the
                // original left/right convention after it shipped. The bubble
                // still sits on its own side of the column via self-end /
                // self-start above — only the text inside is left-aligned.
                'rounded-md px-4 py-2 text-sm leading-relaxed text-left',
                isSent
                  ? 'bg-primary-50 text-white'
                  : 'border border-grey-900 bg-white text-content-primary'
              )}
            >
              <span>{msg.messageBody}</span>
              <div
                className={classNames(
                  'text-[10px] mt-1',
                  isSent ? 'text-white/70' : 'text-content-secondary'
                )}
              >
                {formatMessageTime(msg.createdAt)}
              </div>
            </div>
            {renderMessageContext?.(msg)}
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
};
